import React, { useState } from "react";
import { Button, ButtonGroup, Input, Modal, Select, Toast } from "../../";
import moment from "moment";
import { apis, patch, post } from "../../../Utils/axios";
import { COUNTRIES, PHONE_CODE_OPTIONS } from "../../../Utils/constants";
import { getNameValidationError, limitPhoneDigits, validatePhoneNumberDetailed } from "Utils/utils";
import { formatDobInput } from "../../../Utils/dob";

function formatDateInput(value) {
	const digits = `${value || ""}`.replace(/\D/g, "").slice(0, 8);
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function parseUiDate(value) {
	if (!value) return null;
	const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
	if (!match) return null;
	const dd = parseInt(match[1], 10);
	const mm = parseInt(match[2], 10);
	const yyyy = parseInt(match[3], 10);
	const date = new Date(yyyy, mm - 1, dd);
	if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return null;
	return date;
}

function formatUiToBackendDate(value) {
	if (!value) return "";
	const parts = value.split("/");
	if (parts.length !== 3) return "";
	const [dd, mm, yyyy] = parts;
	return `${dd}${mm}${yyyy}`;
}

function validatePassportDates(expiryUi, issueUi) {
	const currentDate = new Date();
	const expireDate = parseUiDate(expiryUi);
	const issueDate = parseUiDate(issueUi);
	if (!expireDate || !issueDate) return false;
	if (issueDate > currentDate) return false;
	if (expireDate <= issueDate || expireDate <= currentDate) return false;
	return true;
}

function AddGuest({ onReload = () => {}, btnLabel = "+ Add Guest", btnVariant = "primary", btnClass = "", mandatoryField = {} }) {
	const requiredLabel = (text) => (
		<>
			{text} <span className="text-dangerText">*</span>
		</>
	);

	const [NewGuest, setNewGuest] = useState();
	const initial = {
		fName: "",
		lName: "",
		email: "",
		phone: "",
		phoneCode: {},
		gender: {
			label: "Male",
			value: "MALE",
		},
		// TWCP-191: keep DOB empty so admin must enter it
		dob: null,
		passportNumber: "",
		passportIssueDate: "",
		passportExpireDate: "",
		passportCountry: "",
		nationality: "",
		nationalIdNumber: "",
	};
	const [Values, setValues] = useState(initial);
	const [DobInput, setDobInput] = useState("");
	const [Loader, setLoader] = useState();

	const parseGuestDob = (value) => {
		const parsed = moment(value, "DD/MM/YYYY", true);
		if (!parsed.isValid()) {
			return { error: "Please enter date of birth in DD/MM/YYYY format", value: null };
		}
		if (parsed.isAfter(moment().endOf("day"))) {
			return { error: "Date of birth cannot be in the future", value: null };
		}
		return { error: null, value: parsed.valueOf() };
	};

	const onSubmit = async () => {
		const hasPassportInput = Values.passportNumber || Values.passportIssueDate || Values.passportExpireDate || Values.passportCountry;
		const hasGovernmentIdInput = Values.nationality || Values.nationalIdNumber;
		const hasCompletePassport = Values.passportNumber && Values.passportIssueDate && Values.passportExpireDate && Values.passportCountry;
		const hasCompleteGovernmentId = Values.nationality && Values.nationalIdNumber;

		const nameError = getNameValidationError("First name", Values.fName) || getNameValidationError("Last name", Values.lName);
		if (nameError) {
			return Toast.error(nameError);
		}
		if (!Values.gender) {
			return Toast.error("Please select gender");
		}
		if (!Values.email) {
			return Toast.error("Please enter email address");
		}
		if (!DobInput) {
			return Toast.error("Please enter date of birth");
		}
		if (hasPassportInput && !hasCompletePassport) {
			return Toast.error("Please complete all passport details or clear them");
		}
		if (hasGovernmentIdInput && !hasCompleteGovernmentId) {
			return Toast.error("Please complete government ID details or clear them");
		}
		if (mandatoryField.passportValidation && !hasCompletePassport) {
			return Toast.error("Please complete passport details");
		}
		if (mandatoryField.nationalityValidation && !hasCompleteGovernmentId) {
			return Toast.error("Please complete government ID details");
		}
		if (hasCompletePassport) {
			if (!validatePassportDates(Values.passportExpireDate, Values.passportIssueDate)) {
				return Toast.error("Please enter valid passport dates in DD/MM/YYYY format");
			}
		}
		const parsedDob = parseGuestDob(DobInput);
		if (parsedDob.error) {
			return Toast.error(parsedDob.error);
		}

		const phoneResult = await validatePhoneNumberDetailed(Values.phone, Values.phoneCode?.value);
		if (!phoneResult.ok) {
			return Toast.error(phoneResult.message);
		}

		setLoader(true);
		post(
			apis.addGuest,
			{
				...Values,
				gender: Values.gender?.value,
				// Backend expects DOB as YYYY-MM-DD (date only)
				dob: moment(parsedDob.value).format("YYYY-MM-DD"),
				phoneCode: Values.phoneCode?.value,
			},
			(r, e) => {
				if (r) {
					const guestId = r?.id || r?.guest?.id;
					const saveIdentification = hasCompletePassport || hasCompleteGovernmentId;
					if (guestId && saveIdentification) {
						const identificationPayload = {
							guestId,
							...(hasCompletePassport
								? {
										passportNumber: Values.passportNumber,
										passportIssueDate: formatUiToBackendDate(Values.passportIssueDate),
										passportExpireDate: formatUiToBackendDate(Values.passportExpireDate),
										passportCountry: Values.passportCountry,
									}
								: {}),
							...(hasCompleteGovernmentId
								? {
										nationality: Values.nationality,
										nationalIdNumber: Values.nationalIdNumber,
									}
								: {}),
						};
						patch(apis.updateGuestIdentification(guestId), identificationPayload, (identificationResponse, identificationError) => {
							setLoader(false);
							if (identificationResponse) {
								Toast.success("Guest added");
								setNewGuest(false);
								onReload();
								setValues(initial);
							} else if (identificationError) {
								Toast.handleError(identificationError);
							}
						});
						return;
					}
					Toast.success("Guest added");
					setNewGuest(false);
					setLoader(false);
					onReload();
					setValues(initial);
				} else if (e) {
					Toast.handleError(e);
					setLoader(false);
				}
			}
		);
	};

	return (
		<>
			<Button onClick={() => setNewGuest(true)} variant={btnVariant} className={btnClass}>
				{btnLabel}
			</Button>
			<Modal open={NewGuest} onClose={() => setNewGuest()}>
				<div className="p-6">
					<h1>New Guest</h1>
					<div className="mt-4 space-y-4">
						<div className="grid grid-cols-2 gap-x-4">
							<Input noNumbers required value={Values.fName} onChange={(fName) => setValues((prev) => ({ ...prev, fName }))} label={requiredLabel("First Name")} placeholder="First Name" />
							<Input noNumbers required value={Values.lName} onChange={(lName) => setValues((prev) => ({ ...prev, lName }))} label={requiredLabel("Last Name")} placeholder="Last Name" />
						</div>
						<Input required value={Values.email} onChange={(email) => setValues((prev) => ({ ...prev, email: email.toLowerCase() }))} label={requiredLabel("Email")} placeholder="Email Address" />
						<div>
							<label>{requiredLabel("Phone Number")}</label>
							<div className="flex gap-2 mt-2">
								<Select className="max-w-min" placeholder="Code" selected={Values.phoneCode} onChange={(phoneCode) => setValues((prev) => ({ ...prev, phoneCode }))} required label="" options={PHONE_CODE_OPTIONS} />
								<Input className="flex-1" type="number" required value={Values.phone} onChange={(phone) => setValues((prev) => ({ ...prev, phone: limitPhoneDigits(phone) }))} placeholder="Phone" />
							</div>
						</div>
						<div className="grid grid-cols-2 mt-4 gap-x-4">
							<Input
								value={DobInput}
								onChange={(value) => setDobInput(formatDobInput(value))}
								required
								label={requiredLabel("Date of Birth")}
								placeholder="DD/MM/YYYY"
								maxLength={10}
								allowSpecialChars
								inputClass="tracking-wide"
							/>
							<div>
								<label className="block mb-2">{requiredLabel("Gender")}</label>
								<ButtonGroup
									selected={Values.gender}
									onClick={(gender) => setValues((prev) => ({ ...prev, gender }))}
									options={[
										{
											label: "Male",
											value: "MALE",
										},
										{
											label: "Female",
											value: "FEMALE",
										},
									]}
								/>
							</div>
						</div>
						{(mandatoryField.passportValidation || mandatoryField.nationalityValidation) && (
							<div className="pt-2 space-y-4 border-t border-light">
								<div>
									<h4>Identification</h4>
									<div className="mt-1 text-xs text-secondary">Only the fields required by this booking are shown below. Required fields are marked with <span className="text-dangerText font-semibold">*</span>.</div>
								</div>
								{mandatoryField.passportValidation && (
									<div className="space-y-4">
										<h4>Passport Details <span className="text-dangerText">*</span></h4>
										<Input required maxLength={20} value={Values.passportNumber} label={requiredLabel("Passport Number")} placeholder="Passport Number" onChange={(passportNumber) => setValues((prev) => ({ ...prev, passportNumber }))} />
										<Select btnClass="w-full" options={COUNTRIES} required selected={{ value: Values.passportCountry, label: Values.passportCountry }} label={requiredLabel("Passport Country")} placeholder="Select Country" onChange={(country) => setValues((prev) => ({ ...prev, passportCountry: country.value }))} />
										<div className="grid grid-cols-2 gap-x-4">
											<Input allowSpecialChars maxLength={10} required value={Values.passportIssueDate} label={requiredLabel("Issue Date")} placeholder="DD/MM/YYYY" onChange={(passportIssueDate) => setValues((prev) => ({ ...prev, passportIssueDate: formatDateInput(passportIssueDate) }))} />
											<Input allowSpecialChars maxLength={10} required value={Values.passportExpireDate} label={requiredLabel("Expire Date")} placeholder="DD/MM/YYYY" onChange={(passportExpireDate) => setValues((prev) => ({ ...prev, passportExpireDate: formatDateInput(passportExpireDate) }))} />
										</div>
									</div>
								)}
								{mandatoryField.nationalityValidation && (
									<div className="space-y-4">
										<h4>Government ID Details <span className="text-dangerText">*</span></h4>
										<Input required value={Values.nationalIdNumber} label={requiredLabel("National ID")} placeholder="National ID" onChange={(nationalIdNumber) => setValues((prev) => ({ ...prev, nationalIdNumber }))} />
										<Select btnClass="w-full" options={COUNTRIES} required selected={{ value: Values.nationality, label: Values.nationality }} label={requiredLabel("Nationality")} placeholder="Select Nationality" onChange={(nationality) => setValues((prev) => ({ ...prev, nationality: nationality.value }))} />
									</div>
								)}
							</div>
						)}
						<Button onClick={onSubmit} loading={Loader} className="w-full mt-4">
							Add Guest
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
}

export default AddGuest;
